/** Who is streaming right now, and the one way to make them stop.
 *
 * The Dashboard already shows the streams in a corner; this is the same table
 * with room to breathe, and the reason for it is the one kind of load a
 * service cannot read from a row afterwards: an open socket. A stream row is
 * written when the logger connects and deleted when it closes, so the table
 * is the present tense — which is why it re-reads itself every few seconds
 * while the tab is visible and says when it last did.
 *
 * A row can outlive its stream. A logger that lost power leaves a row whose
 * last frame is a minute old, and the service lists it but does not count it
 * as a driver; it is drawn dim here for the same reason. Kicking such a row
 * is how it is cleaned up: the Durable Object is told first, the row is
 * removed either way, and whether the object had anything open to close is
 * reported rather than assumed, because "the stream ended" and "there was no
 * stream" are different facts about what just happened.
 *
 * The empty state tells "nobody is driving" from "this service does not carry
 * live streams" — the type ships switched off, and a table that is empty
 * because of a setting should say which setting.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminLive, getLiveListing, kickLive, spectatePath } from '@/lib/api';
import { count, dateTime, hz, plural, relativeTime } from '@/lib/format';
import type { AdminLiveListing, LiveStream } from '@/lib/types';
import {
  EmptyState,
  ErrorNote,
  LoadingPanel,
  Page,
  PulseDot,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
} from '@/components/ui';
import { AdminHeader, Notice } from '@/components/admin';

/** How often the table re-reads itself while visible. */
const REFRESH_MS = 5000;

const MONO_SMALL = {
  display: 'block',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  color: 'var(--color-neutral-600)',
} as const;

export default function AdminLive() {
  const [listing, setListing] = useState<AdminLiveListing | null>(null);
  const [error, setError] = useState<unknown>(null);
  /** Null until known: whether the server carries live streams at all. */
  const [enabled, setEnabled] = useState<boolean | null>(null);

  /** The stream whose kick is being confirmed. */
  const [arming, setArming] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [kickError, setKickError] = useState<unknown>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);

  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const reply = await adminLive();
      if (!alive.current) return;
      setListing(reply);
      setError(null);
    } catch (thrown) {
      if (alive.current) setError(thrown);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    void getLiveListing()
      .then((reply) => {
        if (alive.current) setEnabled(reply.enabled);
      })
      .catch(() => undefined);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [load]);

  const kick = async (stream: LiveStream) => {
    setBusy(true);
    setKickError(null);
    try {
      const done = await kickLive(stream.user_id, reason.trim() || undefined);
      if (!alive.current) return;
      setNotice(
        done.delivered
          ? { tone: 'ok', text: `${stream.display_name}’s stream ended` }
          : {
              tone: 'warn',
              text: `${stream.display_name}’s row was removed, but the stream object did not confirm: ${done.delivery_error ?? 'no reason given'}`,
            },
      );
      setArming(null);
      setReason('');
      await load();
    } catch (thrown) {
      if (alive.current) setKickError(thrown);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const streams = listing?.streams ?? [];

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section aria-label="Live streams" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className="tabular-nums" style={{ fontSize: 13, color: 'var(--color-neutral-400)' }}>
            {listing ? (
              <>
                {plural(listing.drivers, 'driver')} on air · {plural(listing.spectators, 'spectator')}
                {listing.streams.length > listing.drivers
                  ? ` · ${count(listing.streams.length - listing.drivers)} stale`
                  : ''}
              </>
            ) : (
              'counting…'
            )}
          </span>
          <span
            className="tabular-nums"
            style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--color-neutral-600)' }}
          >
            {listing ? `counted ${relativeTime(listing.generated_at)} · ` : ''}
            refreshes every {REFRESH_MS / 1000} s while this tab is visible
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 12 }}
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>

        {notice ? (
          <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
            {notice.text}
          </Notice>
        ) : null}
        {error ? (
          <ErrorNote error={error} title="the streams could not be read" compact onRetry={() => void load()} />
        ) : null}

        {!listing && !error ? (
          <LoadingPanel label="loading streams" />
        ) : streams.length === 0 ? (
          enabled === false ? (
            <EmptyState
              title="live streams are switched off"
              action={
                <Link className="btn btn-secondary" to="/admin/settings" style={{ fontSize: 12 }}>
                  Open Settings
                </Link>
              }
            >
              The <code>live</code> type ships disabled. Until it is switched on, loggers are not
              offered the toggle and both sockets answer <code>403 type_disabled</code>.
            </EmptyState>
          ) : (
            <EmptyState title="nobody is streaming">
              A logger with <code>live:write</code> opens one socket per session; a row appears the
              moment one does, and leaves when it closes.
            </EmptyState>
          )
        ) : (
          <Table scroll label="Live streams" style={{ fontSize: 13 }}>
            <THead>
              <TR>
                <TH>Driver</TH>
                <TH>Track</TH>
                <TH>Car</TH>
                <TH numeric>Rate</TH>
                <TH numeric>Frames</TH>
                <TH numeric>Watching</TH>
                <TH>Visibility</TH>
                <TH>Started</TH>
                <TH>Last frame</TH>
                <TH>
                  <span className="sr-only">actions</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {streams.map((stream) => {
                const stale = stream.stale === true;
                const open = arming === stream.user_id;
                return (
                  <TR key={stream.user_id} style={stale ? { opacity: 0.55 } : undefined}>
                    <TD nowrap>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PulseDot size={6} tone={stale ? 'neutral' : 'accent'} pulse={!stale} />
                        <span style={{ minWidth: 0 }}>
                          <Link
                            to={spectatePath(stream.user_id)}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                            title="open the spectate page"
                          >
                            {stream.display_name}
                          </Link>
                          <span style={MONO_SMALL}>{stream.user_id}</span>
                        </span>
                      </span>
                    </TD>
                    <TD nowrap title={stream.official_id}>
                      {stream.track_name || stream.official_id || DASH_TEXT}
                    </TD>
                    <TD nowrap muted>
                      {stream.car || DASH_TEXT}
                    </TD>
                    <TD numeric color={stale ? 'var(--color-neutral-500)' : undefined}>
                      {stale ? 'stale' : hz(stream.hz)}
                    </TD>
                    <TD numeric>{count(stream.frames)}</TD>
                    <TD numeric>{count(stream.spectators)}</TD>
                    <TD nowrap>
                      <Tag tone={stream.public ? 'accent' : 'quiet'}>
                        {stream.public ? 'public' : 'private'}
                      </Tag>
                    </TD>
                    <TD nowrap muted title={dateTime(stream.started_at)}>
                      {relativeTime(stream.started_at)}
                    </TD>
                    <TD nowrap muted title={dateTime(stream.last_frame_at)}>
                      {relativeTime(stream.last_frame_at)}
                    </TD>
                    <TD nowrap align="right">
                      {open ? (
                        <form
                          style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}
                          onSubmit={(event) => {
                            event.preventDefault();
                            void kick(stream);
                          }}
                        >
                          <label className="sr-only" htmlFor={`kick-${stream.user_id}`}>
                            reason, shown to the driver
                          </label>
                          <input
                            id={`kick-${stream.user_id}`}
                            className="input"
                            value={reason}
                            autoFocus
                            placeholder="reason, shown to the driver"
                            style={{ minHeight: 28, fontSize: 12, width: 200 }}
                            onChange={(event) => setReason(event.currentTarget.value)}
                          />
                          <button type="submit" className="btn btn-danger" style={{ fontSize: 11.5 }} disabled={busy}>
                            {busy ? 'Ending…' : 'End stream'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}
                            disabled={busy}
                            onClick={() => {
                              setArming(null);
                              setKickError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}
                          disabled={busy}
                          onClick={() => {
                            setArming(stream.user_id);
                            setReason('');
                            setKickError(null);
                          }}
                        >
                          {stale ? 'remove' : 'kick'}
                        </button>
                      )}
                      {open && kickError ? (
                        <div style={{ marginTop: 6, textAlign: 'left' }}>
                          <ErrorNote error={kickError} title="the stream was not ended" compact />
                        </div>
                      ) : null}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}

        {listing && streams.length > 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-600)', textWrap: 'pretty' }}>
            a row with no frame for {listing.stale_after_s} s is stale: still listed, not counted as a
            driver, and removed the same way a stream is kicked
          </p>
        ) : null}
      </section>
    </Page>
  );
}

/** What an empty cell prints. Imported as a name so the table does not
 *  depend on the formatter for a single glyph. */
const DASH_TEXT = '—';
